import { useMemo } from 'react';
import { type Block, type Inline, type ListItem, parseReport } from '../report/markdown';

/**
 * The report, in the page (docs/DECISIONS.md ZD-125 §5).
 *
 * Every string the parser produced is rendered as a React text node — there is
 * no `dangerouslySetInnerHTML` here and must not be, because the document
 * carries the client's own object names from the client's own documents, and
 * a name that is markup would otherwise run in the console of whoever reads
 * the report next. The parser's fidelity is asserted in `markdown.test.ts`;
 * this maps its blocks to elements and adds nothing to the text.
 */

function Runs({ inlines }: { inlines: readonly Inline[] }): React.JSX.Element {
  return (
    <>
      {inlines.map((run, index) => {
        if (run.kind === 'strong') return <strong key={index}>{run.text}</strong>;
        if (run.kind === 'em') return <em key={index}>{run.text}</em>;
        return <span key={index}>{run.text}</span>;
      })}
    </>
  );
}

function Items({ items }: { items: readonly ListItem[] }): React.JSX.Element {
  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>
          <Runs inlines={item.inlines} />
          {item.children.length === 0 ? null : <Items items={item.children} />}
        </li>
      ))}
    </ul>
  );
}

function BlockView({ block }: { block: Block }): React.JSX.Element {
  switch (block.kind) {
    case 'heading': {
      const Tag = block.level === 1 ? 'h1' : block.level === 2 ? 'h2' : 'h3';
      return (
        <Tag>
          <Runs inlines={block.inlines} />
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p>
          <Runs inlines={block.inlines} />
        </p>
      );
    case 'quote':
      return (
        <blockquote>
          <Runs inlines={block.inlines} />
        </blockquote>
      );
    case 'list':
      return <Items items={block.items} />;
  }
}

export function ReportReader({ text }: { text: string }): React.JSX.Element {
  const blocks = useMemo(() => parseReport(text), [text]);
  return (
    <article className="report" aria-label="The report">
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} />
      ))}
    </article>
  );
}
